import React from 'react';
import { Avatar, Box, Typography, useTheme } from '@ublend-npm/aulaui-core';
import type { Post } from '@app/types/core';
import formatDate from '@core/utils/formatDate';
import EditorReadOnly from '../../common/EditorPlus/EditorReadOnly.container';
import PostReactions from './PostReactions';
import { createStyles } from './CommonRoomResponsive.styles';

type PostUser = Post['user'] & {
  firstName?: string;
  lastName?: string;
  avatar?: string;
};

type CommonRoomPostResponsiveProps = {
  post: Post;
  readOnly?: boolean;
};

const CommonRoomPostResponsive = ({
  post,
  readOnly = false,
}: CommonRoomPostResponsiveProps) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const { user: postUser } = post;
  const user: PostUser = postUser;
  const fullName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.email ||
    'Deleted User';
  const initials =
    [user?.firstName, user?.lastName]
      .filter(Boolean)
      .map((name) => name?.charAt(0).toUpperCase())
      .join('') || 'U';

  const renderContent = () => {
    if (!post.content) return null;

    try {
      return (
        <EditorReadOnly
          itemId={post.objectId}
          rawEditorState={JSON.parse(post.content)}
          itemType={post.feedType}
        />
      );
    } catch (error) {
      return <Typography variant="body2">{post.content}</Typography>;
    }
  };

  return (
    <Box
      component="article"
      aria-label={`Post by ${fullName}`}
      sx={styles.postCard}
    >
      <Box sx={styles.postHeader}>
        <Avatar
          altText={fullName}
          fallbackText={initials}
          kind="circular"
          src={user?.avatar}
          sx={{ flexShrink: 0, height: 40, width: 40 }}
        />
        <Box sx={styles.postAuthorDetails}>
          <Typography component="h2" variant="subtitle1" noWrap>
            {fullName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDate(post.createdAt)}
          </Typography>
        </Box>
      </Box>

      <Box sx={styles.postContent}>{renderContent()}</Box>

      <Box sx={styles.postReactionRow}>
        <PostReactions postId={post.objectId} readOnly={readOnly} />
      </Box>
    </Box>
  );
};

export default CommonRoomPostResponsive;
